/**
 * Simple isObject check.
 * From: http://stackoverflow.com/questions/27936772/deep-object-merging-in-es6-es7
 * @param item
 * @returns {boolean}
 */
export const IsObject = (item) => {
  return (
    item && typeof item === 'object' && !Array.isArray(item) && item !== null
  )
}

export const FileToBase64 = (file, whenDone) => {
  const reader = new FileReader()
  let data = ''
  reader.addEventListener(
    'load',
    () => {
      data = reader.result.split(',')[1]
      whenDone(data)
    },
    false
  )
  reader.readAsDataURL(file)
}

/**
 * Deep merge two objects.
 * From: http://stackoverflow.com/questions/27936772/deep-object-merging-in-es6-es7
 * @param target
 * @param source
 */
export const MergeDeep = (target, source) => {
  let output = Object.assign({}, target)
  if (IsObject(target) && IsObject(source)) {
    Object.keys(source).forEach((key) => {
      if (IsObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] })
        } else {
          output[key] = MergeDeep(target[key], source[key])
        }
      } else {
        Object.assign(output, { [key]: source[key] })
      }
    })
  }
  return output
}

/**
 * Deep copy an object (uses JSON so only data objects are applicable)
 * @param {Object} obj the object to copy
 * @returns a copy
 */
export const DeepCopy = <T>(obj: T): T => JSON.parse(JSON.stringify(obj))

export const DownloadFileFromBlob = (fileName) => {
  return (blob) => {
    // For Microsoft Edge
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    document.body.appendChild(a)
    a.style.display = 'none'
    a.href = url
    a.download = fileName
    a.click()

    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }
}

export const DownloadFileFromString = (content, fileName, contentType) => {
  // For Microsoft Edge
  const url = encodeURI('data:' + contentType + ';charset=utf-8,' + content)
  const a = document.createElement('a')
  document.body.appendChild(a)
  a.href = url // or window.URL.createObjectURL(new Blob([ content ], {type:contentType}))
  a.download = fileName
  a.click()

  document.body.removeChild(a)
}

export const OpenBlobInNewWindow = (blob: Blob, windowRef?: Window) => {
  const url = window.URL.createObjectURL(blob)
  if (windowRef) {
    windowRef.location.replace(url)
  } else {
    windowRef.open(url)
  }
}

export const DownloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  document.body.appendChild(a)
  a.href = url
  a.download = filename
  a.click()
  document.body.removeChild(a)
}

export const IsElementVisible = (e) => {
  return e.offsetLeft > 0
}

export const DefaultDateFormat = (date, onNull = '-') => {
  if (date === null || date === undefined) {
    return onNull
  }
  // From: http://allenfang.github.io/react-bootstrap-table/example.html
  if (typeof date === 'string') date = parseInt(date)
  if (!(date instanceof Date)) date = new Date(date)
  return (
    date.getFullYear() +
    '-' +
    ('0' + (date.getMonth() + 1)).slice(-2) +
    '-' +
    ('0' + date.getDate()).slice(-2) +
    ' ' +
    ('0' + date.getHours()).slice(-2) +
    ':' +
    ('0' + date.getMinutes()).slice(-2) +
    ':' +
    ('0' + date.getSeconds()).slice(-2)
  )
}

export const CanadianDateFormat = (date) => {
  // From: http://allenfang.github.io/react-bootstrap-table/example.html
  //if (Object.prototype.toString.apply(date) !== "[object Date]")
  if (typeof date === 'string') date = parseInt(date)
  if (!(date instanceof Date)) date = new Date(date)
  return (
    ('0' + date.getDate()).slice(-2) +
    '/' +
    ('0' + (date.getMonth() + 1)).slice(-2) +
    '/' +
    date.getFullYear() +
    ' ' +
    ('0' + date.getHours()).slice(-2) +
    ':' +
    ('0' + date.getMinutes()).slice(-2) +
    ':' +
    ('0' + date.getSeconds()).slice(-2)
  )
}

export const DateOnly = (date) => {
  if (typeof date === 'string') date = parseInt(date)
  if (!(date instanceof Date)) date = new Date(date)

  let hours = date.getHours() % 12
  if (hours === 0) hours = 12

  return (
    date.getFullYear() +
    '-' +
    ('0' + (date.getMonth() + 1)).slice(-2) +
    '-' +
    ('0' + date.getDate()).slice(-2)
  )
}

export const TwelveHourTimeOnly = (date) => {
  if (typeof date === 'string') date = parseInt(date)
  if (!(date instanceof Date)) date = new Date(date)

  let hours = date.getHours() % 12
  if (hours === 0) hours = 12
  let amPm = date.getHours() >= 12 ? 'PM' : 'AM'

  return (
    ('0' + hours).slice(-2) +
    ':' +
    ('0' + date.getMinutes()).slice(-2) +
    ':' +
    ('0' + date.getSeconds()).slice(-2) +
    ' ' +
    amPm
  )
}

export const IsDescendantOrSame = (parent, child) => {
  return parent === child || IsDescendant(parent, child)
}

export const IsDescendant = (parent, child) => {
  let node = child.parentNode
  while (node != null) {
    if (node == parent) {
      return true
    }
    node = node.parentNode
  }
  return false
}

export const AlphabeticalChildSort =
  (childName) =>
  (a, b, order = 'asc') => {
    const aName = a[childName].toLowerCase(),
      bName = b[childName].toLowerCase()
    if (aName > bName) return order === 'asc' ? 1 : -1
    else if (aName < bName) return order === 'asc' ? -1 : 1
    return 0
  }

export const CapitalizeFirstLetterOnly = (s) =>
  s.substr(0, 1).toUpperCase() + s.substr(1).toLowerCase()

export const Round = (value, precision = 0) => {
  const multiplier = Math.pow(10, precision)
  return Math.round(value * multiplier) / multiplier
}

export const NumberFormat = (val, precision = 2) =>
  val.toFixed(precision).replace(/(\d)(?=(\d{3})+\.)/g, '$1,')
export const MoneyFormat = (val, precision = 2) =>
  '$' + NumberFormat(val, precision)

// Courtesy of https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript-jquery
export const StringHash = (str) => {
  var hash = 0,
    i,
    chr
  if (str.length === 0) return hash
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0 // Convert to 32bit integer
  }
  return hash
}

// Sourced from: https://gist.github.com/DiegoSalazar/4075533
// takes the form field value and returns true on valid number
export const ValidCreditCard = (value) => {
  // accept only digits, dashes or spaces
  if (/[^0-9-\s]+/.test(value)) {
    return false
  }

  // The Luhn Algorithm. It's so pretty.
  let nCheck = 0,
    bEven = false
  value = value.replace(/\D/g, '')

  for (var n = value.length - 1; n >= 0; n--) {
    let cDigit = value.charAt(n),
      nDigit = parseInt(cDigit, 10)

    if (bEven) {
      if ((nDigit *= 2) > 9) {
        nDigit -= 9
      }
    }

    nCheck += nDigit
    bEven = !bEven
  }

  return nCheck % 10 === 0
}

export const GetTimeDifferenceString = (time1: Date, time2: Date) => {
  var hourPart = time2.getHours() - time1.getHours()
  if (hourPart < 0) {
    hourPart += 24
  }
  var minutePart = time2.getMinutes() - time1.getMinutes()
  if (minutePart < 0) {
    hourPart = hourPart > 0 ? hourPart - 1 : 23
    minutePart += 60
  }
  return hourPart + ':' + (minutePart > 10 ? minutePart : '0' + minutePart)
}

export const GetUserMedia = () => {
  const navigator: any = window.navigator
  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    return navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices)
  }
  const getUserMedia =
    navigator.getUserMedia ||
    navigator.webkitGetUserMedia ||
    navigator.mozGetUserMedia ||
    navigator.msGetUserMedia
  return getUserMedia ? getUserMedia.bind(navigator) : null
}

export const IsMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
}
